/**
 * Embudo del checkout de la tienda online (spec 2026-10-19 §5.9). Mide los dos
 * caminos (v1 = checkout de siempre, v2 = por pasos) para decidir con datos si
 * se prende v2 para todas las tiendas (§5.1).
 *
 * SIN datos personales: el lote lleva exactamente las columnas de
 * `checkout_funnel_events` y nada más. Nunca lanza: medir no puede romper la venta.
 */
import { z } from 'zod'

export const FUNNEL_STEPS = ['cart', 'fulfillment', 'when', 'address', 'customer', 'tip', 'donation', 'payment', 'confirm'] as const
export type FunnelStep = (typeof FUNNEL_STEPS)[number]

/**
 * Paso del checkout → paso del embudo. El paso «Si algo no está disponible»
 * (`substitutions`, justo después del carrito) se mide como parte del carrito:
 * `checkout_funnel_events.step` tiene un CHECK con esta lista y sumarle un valor
 * pide migración. Cualquier otro paso pasa tal cual.
 */
export function funnelStepOf(step: FunnelStep | 'substitutions'): FunnelStep {
  return step === 'substitutions' ? 'cart' : step
}
export type FunnelPath = 'v1' | 'v2'
export type FunnelFulfillment = 'pickup' | 'delivery' | 'ship' | 'table'

const eventSchema = z
  .object({
    event: z.enum(['step_viewed', 'step_advanced', 'abandoned', 'paid']),
    step: z.enum(FUNNEL_STEPS),
    fulfillment: z.enum(['pickup', 'delivery', 'ship', 'table']).nullable(),
    scheduled: z.boolean(),
    order_id: z.string().guid().nullable(),
    displayed_total_cents: z.number().int().min(0).max(10_000_000).nullable(),
  })
  .strict()

export const checkoutEventBatchSchema = z
  .object({
    checkout_session_id: z.string().uuid(),
    // .guid(), no .uuid(): Zod exige RFC 9562 y prod tiene ids de local sembrados
    // a mano (pepitos-waao) — ver __tests__/location-id-guid-contract.test.ts.
    location_id: z.string().guid(),
    path: z.enum(['v1', 'v2']),
    events: z.array(eventSchema).min(1).max(20),
  })
  .strict()

export type CheckoutEventBatch = z.infer<typeof checkoutEventBatchSchema>
export type CheckoutEventRow = CheckoutEventBatch['events'][number] & {
  checkout_session_id: string
  location_id: string
  path: FunnelPath
}

export function toCheckoutEventRows(b: CheckoutEventBatch): CheckoutEventRow[] {
  return b.events.map((e) => ({ ...e, checkout_session_id: b.checkout_session_id, location_id: b.location_id, path: b.path }))
}

export type FunnelCtx = { fulfillment: FunnelFulfillment; scheduled: boolean }

type Deps = {
  locationId: string
  path: FunnelPath
  url?: string
  /** Inyectables para tests. */
  beacon?: (url: string, data: BodyInit) => boolean
  fetchFn?: typeof fetch
  newId?: () => string
}

export type CheckoutFunnel = {
  /** uuid de esta sesión de checkout ('' si el navegador no puede generarlo). */
  sessionId: string
  /** El pago sigue en otro host (dominio propio): no hay abandono que medir acá;
   *  el `paid` lo manda la vuelta, con este mismo `sessionId` (viaja en el stash). */
  handoff: () => void
  viewed: (step: FunnelStep, ctx: FunnelCtx) => void
  advanced: (step: FunnelStep, ctx: FunnelCtx) => void
  /** Una sola vez por sesión y nunca después de `paid`. */
  abandoned: (step: FunnelStep, ctx: FunnelCtx) => void
  /** Una sola vez por sesión. */
  paid: (ctx: FunnelCtx & { orderId: string; displayedTotal: number }) => void
}

export function makeCheckoutFunnel({ locationId, path, url = '/api/online/checkout-events', beacon, fetchFn, newId }: Deps): CheckoutFunnel {
  let sessionId = ''
  try {
    sessionId = newId ? newId() : crypto.randomUUID()
  } catch {
    // sin crypto.randomUUID no se mide: el servidor rechazaría un id inválido igual
  }
  let closed = false

  function send(e: CheckoutEventBatch['events'][number]) {
    if (!sessionId) return
    try {
      const body = JSON.stringify({ checkout_session_id: sessionId, location_id: locationId, path, events: [e] } satisfies CheckoutEventBatch)
      const doBeacon =
        beacon ??
        (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
          ? navigator.sendBeacon.bind(navigator)
          : undefined)
      if (doBeacon) {
        try {
          const data = typeof Blob !== 'undefined' ? new Blob([body], { type: 'application/json' }) : body
          if (doBeacon(url, data)) return
        } catch {
          // cae al fetch
        }
      }
      const doFetch = fetchFn ?? (typeof fetch !== 'undefined' ? fetch : undefined)
      if (!doFetch) return
      void doFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {})
    } catch {
      // jamás throw
    }
  }

  const base = (step: FunnelStep, ctx: FunnelCtx) => ({
    step,
    fulfillment: ctx.fulfillment,
    scheduled: ctx.scheduled,
    order_id: null,
    displayed_total_cents: null,
  })

  return {
    sessionId,
    handoff: () => {
      closed = true
    },
    viewed: (step, ctx) => {
      if (!closed) send({ event: 'step_viewed', ...base(step, ctx) })
    },
    advanced: (step, ctx) => {
      if (!closed) send({ event: 'step_advanced', ...base(step, ctx) })
    },
    abandoned: (step, ctx) => {
      if (closed) return
      closed = true
      send({ event: 'abandoned', ...base(step, ctx) })
    },
    paid: (ctx) => {
      if (closed) return
      closed = true
      send({
        event: 'paid',
        step: 'payment',
        fulfillment: ctx.fulfillment,
        scheduled: ctx.scheduled,
        order_id: ctx.orderId,
        displayed_total_cents: Math.round(ctx.displayedTotal * 100),
      })
    },
  }
}
